import { useDispatch, useSelector } from 'react-redux';
import React from 'react';
import { NavisionExport } from '@inter-ikea-kompis/navision-export';
import { SalesSystemEnum } from '@inter-ikea-kompis/enums';
import { getShoppingItemsFromTAC } from './util/aactools/kompisConvert';
import { init } from './state/init/initActions.ts';
import BodyAttributes from './components/BodyAttributes';
import LoadingError from './components/Lightbox/LoadingError';
import LoadingSpinner from './components/LoadingSpinner';
import platform from './util/platform';
import ResizeHandler from './components/ResizeHandler/ResizeHandler';
import ScreenSaver from './components/ScreenSaver/ScreenSaver.tsx';
import tacHelpers from './state/tac/tacHelpers';
import { selectCurrentView } from './state/navigation/navigationSelectors';
import { selectInitDone, selectInitFailed } from './state/init/initSelectors';
import { selectTac } from './state/tac/tacSelectors';
import {
  selectDialog,
  selectDialogExists,
} from './state/dialog/dialogSelectors';
import {
  selectKompisDexfSettings,
  selectDexfSettingsHaveBeenLoaded,
} from './state/dexfSettings/dexfSettingsSelectors';
import {
  selectIsKiosk,
  selectUserAgent,
} from './state/userAgent/userAgentSelectors';
import views from './settings/views/views';
import { actionSetSceneImage } from './state/summary/summaryActions';
import { KompisKeyboard } from '@inter-ikea-kompis/react-components';
import useKeyboard from './util/useKeyboard';
import { KeyboardTypeEnum } from '@inter-ikea-kompis/keyboard-manager';
import { selectLocale } from './state/summary/summarySelectors';
import { translate } from './services/L10n';
import { t } from './translations';
import Survey from './components/Survey/Survey';
import SheetLoader from './components/Sheets/SheetLoader';
import { SkapaTheme } from '@inter-ikea-kompis/themes/lib';
import constants from './settings/constants';
import { useKioskIntegration } from './hooks/useKioskIntegration';

const App = () => {
  const initDone = useSelector(selectInitDone);
  const initFailed = useSelector(selectInitFailed);
  const currentView = useSelector(selectCurrentView);
  const dialog = useSelector(selectDialog);
  const dialogExists = useSelector(selectDialogExists);
  const tac = useSelector(selectTac);
  const kompisDexfSettingsLocalization = useSelector(
    selectKompisDexfSettings
  )?.localization;
  const dexfSettingsHaveBeenLoaded = useSelector(
    selectDexfSettingsHaveBeenLoaded
  );
  const userAgent = useSelector(selectUserAgent);
  const isKiosk = useSelector(selectIsKiosk);
  const locale = useSelector(selectLocale);
  const { integration, isUpptackaOrIpexGallery, isUpptacka, isGallery } =
    useKioskIntegration();
  const dispatch = useDispatch();

  const [buildScene, setBuildScene] = React.useState(null);
  const [detailedSceneImage, setDetailedSceneImage] = React.useState(null);
  const navisionExport = React.useRef(null);
  const [showKeyboard, setShowKeyboard] = React.useState(false);
  const [keyboardInputType, setKeyboardInputType] = React.useState(
    KeyboardTypeEnum.text
  );

  const { onDone, onKeyPress, registerRootFunctions } = useKeyboard;

  const shouldRenderScreensaver = platform.isKiosk && !isUpptackaOrIpexGallery;
  const shouldRenderKeyboard = platform.isKiosk && !isUpptacka;

  /**
   * Set scaling percentage
   */
  const setScaling = percentage => {
    const percentageString = percentage + '%';
    document.documentElement.style.fontSize = percentageString;
  };

  /**
   * Handles kiosk keyboard key press
   *
   * @param keyCode
   * @returns {any}
   */
  const onVirtualKeyPress = ({ detail: { keyCode } }) => onKeyPress(keyCode);

  /**
   * Start navision
   * @returns {Promise<false|void>}
   */
  const startNavision = async () => {
    navisionExport.current = new NavisionExport();
    kompisDexfSettingsLocalization.salesSystem !== SalesSystemEnum.isell &&
      navisionExport.current.connect();
  };

  React.useEffect(() => {
    dispatch(init());
    const upptackaInteraction = () => integration?.userInteraction();

    if (isKiosk) {
      registerRootFunctions({
        setInputType: setKeyboardInputType,
        keyboardToggle: setShowKeyboard,
      });

      setScaling(constants.KIOSK_SCALING_PERCENTAGE);

      document.addEventListener('click', upptackaInteraction);
    }

    return () => {
      document.removeEventListener('click', upptackaInteraction);
    };
  }, []);

  React.useEffect(() => {
    tac && tacHelpers.clearCache(tac);
  }, [tac]);

  React.useEffect(() => {
    if (kompisDexfSettingsLocalization && !navisionExport.current) {
      startNavision();
    }
  }, [kompisDexfSettingsLocalization]);

  React.useEffect(() => {
    tac &&
      navisionExport.current &&
      navisionExport.current.setShoppingItems(getShoppingItemsFromTAC(tac));
  }, [tac]);

  React.useEffect(() => {
    if (initDone) {
      integration?.applicationLoaded();
      isGallery && integration?.handleCountdownBanner();
    }
  }, [initDone]);

  /**
   * Capture screen
   * @param scene
   */
  const captureScene = scene => {
    setBuildScene(scene);
    setDetailedSceneImage(scene.getSceneImage());
    dispatch(actionSetSceneImage(scene.getSceneImage(true)));
  };

  const _onDone = () => {
    onDone();
    setShowKeyboard(false);
  };

  /**
   * Render main content
   * @returns {JSX.Element}
   */
  const renderMainContent = () => {
    const View = views[currentView].component;
    const Dialog = dialog?.type;

    return (
      <React.Fragment>
        <BodyAttributes />
        <ResizeHandler>
          <Survey />
          <View
            userAgent={userAgent}
            captureScene={captureScene}
            detailedSceneImage={detailedSceneImage}
            buildScene={buildScene}
          />
          <SheetLoader />
        </ResizeHandler>
        {dialogExists && <Dialog />}
      </React.Fragment>
    );
  };

  if (!initDone)
    return (
      <ResizeHandler>
        {dexfSettingsHaveBeenLoaded && <LoadingSpinner />}
      </ResizeHandler>
    );

  if (initFailed) {
    isUpptackaOrIpexGallery && integration?.error();
    return <LoadingError />;
  }

  return (
    <React.Fragment>
      {shouldRenderScreensaver && <ScreenSaver />}
      {shouldRenderKeyboard && (
        <KompisKeyboard
          onKeyPressed={onVirtualKeyPress}
          onDone={_onDone}
          visible={showKeyboard}
          type={keyboardInputType}
          theme={SkapaTheme}
          locale={locale}
          doneButtonLabel={translate(t.DONE)}
        />
      )}
      {renderMainContent()}
    </React.Fragment>
  );
};

export default App;
